import React, { useCallback, useEffect, useState } from "react"

import { useMetricDirectory } from "@/metrics/registry"
import { AddMetricsToRunButton, type RerunMetricsById } from "@/metrics/rerun"
import { Button, Card, CardContent } from "@coval/ui"
import { ChevronLeft } from "lucide-react"

import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { usePageShortcuts } from "@/hooks/usePageShortcuts"
import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { MetricViews } from "@/types/MetricViewConfig"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import type { ComparisonPlotConfig } from "@/utils/comparisonPlot"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"
import type { MetricLayoutConfig } from "@/utils/metricLayout"

import type { CompareBy } from "../../compare-by"
import { useHighlightValue } from "../hooks/useHighlightValue"
import type { RunResultsMetricRenderAdaptersById } from "../types"
import { MetricCardSection } from "./MetricCardSection"
import { MetricLayoutSections } from "./MetricLayoutSections"
import { MetricCardSkeletonGrid } from "./RunMetricCardsSkeleton"

/**
 * Hook to determine if container should use single column layout.
 * Returns true when container width is 35% or less of viewport width.
 *
 * Uses a callback ref pattern to properly subscribe/unsubscribe observers
 * when the DOM node changes.
 */
function useResponsiveColumns(): {
  useSingleColumn: boolean
  setContainerEl: (el: HTMLDivElement | null) => void
} {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const [useSingleColumn, setUseSingleColumn] = useState(false)

  useEffect(() => {
    if (!containerEl) {
      return
    }

    const checkWidth = () => {
      const containerWidth = containerEl.getBoundingClientRect().width
      const viewportWidth = window.innerWidth
      const widthPercentage = (containerWidth / viewportWidth) * 100
      setUseSingleColumn(widthPercentage <= 35)
    }

    // Initial check
    checkWidth()

    // Set up ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(checkWidth)
    resizeObserver.observe(containerEl)

    // Also listen to window resize for viewport changes
    window.addEventListener("resize", checkWidth)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", checkWidth)
    }
  }, [containerEl])

  return { useSingleColumn, setContainerEl }
}

interface RunMetricCardsProps {
  runRows: RunRowLike[]
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  isLoading?: boolean
  selectedSimulationOutputIds?: string[]
  /** When provided, in-depth view selection is controlled by the parent (e.g. to scope the table to this metric). */
  selectedMetricId?: string | null
  onSelectedMetricIdChange?: (metricId: string | null) => void
  /** When provided (e.g. on multi-run page), metric cards use ComparedMetricCardSection so graphs can show comparison by dimension. */
  compareBy?: CompareBy | null
  onChartBarFilter?: (filter: ChartBarFilter) => void
  activeChartBarFilters?: ChartBarFilter[]
  /** Per-metric visibility and threshold config. Metrics absent from the record default to visible. */
  metricViews?: MetricViews
  onMetricViewsChange?: (metricViews: MetricViews) => void
  /** When provided, the drill-in view shows a Rerun Metric button scoped to the selected metric. */
  rerunMetricRecordsById?: RerunMetricsById | null
  /** Persisted user arrangement of cards into ordered sections (report context). Absent = default category template. */
  metricLayout?: MetricLayoutConfig
  onMetricLayoutChange?: (layout: MetricLayoutConfig) => void
  /** When true, sections/cards show drag handles and reorder controls. */
  editLayout?: boolean
  /** Renders a comparison-plot block (report context). Absent = no plot blocks. */
  renderPlotBlock?: (plot: ComparisonPlotConfig) => React.ReactNode
  /** Builds a new plot config; when set, an "Add plot" control is shown in edit mode. */
  createPlot?: () => ComparisonPlotConfig
  /** Notifies the new plot's id so the parent can select it for editing. */
  onPlotAdded?: (plotId: string) => void
}

/** Display names for metric categories on run details (match UI spec: Timing & Latency, Conversation Length, Custom). */
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  latency: "Timing & Latency",
  "conversation length": "Conversation Length",
  other: "Custom",
}

function formatCategoryTitle(category: string): string {
  const normalized = category.trim().toLowerCase()
  return (
    CATEGORY_DISPLAY_NAMES[normalized] ??
    category
      .split(" ")
      .map((word) =>
        word === word.toUpperCase() && word.length > 1
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join(" ")
  )
}

export const RunMetricCards: React.FC<RunMetricCardsProps> = ({
  runRows,
  metricRenderAdaptersById,
  isLoading = false,
  selectedSimulationOutputIds = [],
  selectedMetricId: controlledSelectedMetricId,
  onSelectedMetricIdChange,
  compareBy = null,
  onChartBarFilter,
  activeChartBarFilters,
  metricViews,
  rerunMetricRecordsById = null,
  metricLayout,
  onMetricLayoutChange,
  editLayout = false,
  renderPlotBlock,
  createPlot,
  onPlotAdded,
}) => {
  const { useSingleColumn, setContainerEl } = useResponsiveColumns()
  const { byManager } = useMetricDirectory()

  const [internalSelectedMetricId, setInternalSelectedMetricId] = useState<
    string | null
  >(null)
  const selectedMetricId =
    onSelectedMetricIdChange != null
      ? (controlledSelectedMetricId ?? null)
      : internalSelectedMetricId
  const setSelectedMetricId = useCallback(
    (id: string | null) => {
      if (onSelectedMetricIdChange != null) {
        onSelectedMetricIdChange(id)
      } else {
        setInternalSelectedMetricId(id)
      }
    },
    [onSelectedMetricIdChange],
  )

  useEscapeShortcut({
    description: "Back to metrics",
    enabled: !!selectedMetricId,
    handler: () => setSelectedMetricId(null),
    priority: ESCAPE_SHORTCUT_PRIORITY.RESULT_SURFACE,
  })

  const pageShortcuts = React.useMemo(
    () =>
      selectedMetricId
        ? [
            {
              action: () => setSelectedMetricId(null),
              key: "b",
              label: "Back to metrics",
            },
          ]
        : [],
    [selectedMetricId, setSelectedMetricId],
  )

  usePageShortcuts(pageShortcuts)

  // When the user scopes the report by selecting sims (via the right-pane
  // Scope / Rerun selection), narrow the cards' inputs so the averages and
  // distributions reflect just that subset. Empty selection = full scope.
  const scopedRunRows = React.useMemo(() => {
    if (selectedSimulationOutputIds.length === 0) {
      return runRows
    }
    const ids = new Set(selectedSimulationOutputIds)
    return runRows.filter(
      (row) =>
        typeof row.simulation_output_id === "string" &&
        ids.has(row.simulation_output_id),
    )
  }, [runRows, selectedSimulationOutputIds])

  // Derive the canonical metric-id order from the full row set so card
  // order stays stable as the user scopes — only the values inside each
  // card should react to scope, not which cards exist or where they sit.
  const allMetricIds = React.useMemo(() => {
    const metricIds = new Set<string>()
    runRows.forEach((row) => {
      if (row.metric_outputs) {
        Object.keys(row.metric_outputs).forEach((metricId) => {
          metricIds.add(metricId)
        })
      }
    })
    return Array.from(metricIds)
  }, [runRows])

  // Filter to only include metrics that have valid data and records
  const validMetricIds = React.useMemo(() => {
    return allMetricIds.filter((metricId) => {
      const metricRecord = metricRenderAdaptersById[metricId]
      if (!metricRecord) {
        return false
      }

      const metricOutputs = scopedRunRows
        .map((row) => row.metric_outputs?.[metricId])
        .filter(
          (output) =>
            output !== undefined &&
            (output.error_status === MetricErrorStatus.COMPLETED ||
              !output.error_status),
        )

      return metricOutputs.length > 0
    })
  }, [allMetricIds, metricRenderAdaptersById, scopedRunRows])

  // Filter by metricViews visibility (absent = visible)
  const visibleMetricIds = React.useMemo(() => {
    if (!metricViews || Object.keys(metricViews).length === 0) {
      return validMetricIds
    }
    return validMetricIds.filter((id) => {
      const entry = metricViews[id] as { visible?: boolean } | undefined
      return entry?.visible !== false
    })
  }, [validMetricIds, metricViews])

  // Group valid metrics by their category
  const groupedMetrics = React.useMemo(() => {
    const groups: Record<string, string[]> = {}

    visibleMetricIds.forEach((metricId) => {
      const metricRecord = metricRenderAdaptersById[metricId]
      if (!metricRecord) {
        return
      }

      // Prefer the catalog's functional category; fall back to the per-instance value.
      // `||` (not `??`) so an empty-string category is treated as missing.
      const catalogCategory =
        byManager[metricRecord.metric_manager ?? ""]?.category
      const rawCategory =
        catalogCategory || (metricRecord.category as string) || "other"
      const key = formatCategoryTitle(rawCategory)
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(metricId)
    })

    return groups
  }, [visibleMetricIds, metricRenderAdaptersById, byManager])

  const inDepthHighlightValue = useHighlightValue(
    scopedRunRows,
    selectedMetricId ?? "",
    selectedMetricId ? metricRenderAdaptersById[selectedMetricId] : undefined,
  )

  if (isLoading) {
    return (
      <div ref={setContainerEl}>
        <MetricCardSkeletonGrid count={4} useSingleColumn={useSingleColumn} />
      </div>
    )
  }

  if (runRows.length === 0) {
    return null
  }

  if (selectedMetricId) {
    const metricRecord = metricRenderAdaptersById[selectedMetricId]
    const metricOutputs: RunMetricOutput[] = scopedRunRows
      .map((row) => row.metric_outputs?.[selectedMetricId])
      .filter(
        (output): output is RunMetricOutput =>
          output !== undefined &&
          (output.error_status === MetricErrorStatus.COMPLETED ||
            !output.error_status),
      )

    const inDepthContent =
      compareBy != null
        ? (metricRecord?.renderComparedInDepthRunReview?.(
            metricOutputs,
            scopedRunRows,
            compareBy,
          ) ?? null)
        : (metricRecord?.renderInDepthRunReview?.(metricOutputs, {
            runRows: scopedRunRows,
            highlightValue: inDepthHighlightValue,
            onBarClick: onChartBarFilter,
            activeFilters: activeChartBarFilters,
          }) ?? null)

    const allDrillInSimulationOutputIds = runRows
      .map((row) => row.simulation_output_id)
      .filter((id): id is string => typeof id === "string" && id.length > 0)

    return (
      <div ref={setContainerEl} className="p-6 pt-6 bg-surface-contrast">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => setSelectedMetricId(null)}
            title="Back to metrics (B or Escape)"
            aria-label="Back to metrics"
          >
            <ChevronLeft />
            <kbd className="inline-flex min-w-5 items-center justify-center px-1.5 py-0.5 text-[10px] font-medium bg-surface-0 rounded">
              b
            </kbd>
          </Button>
          {rerunMetricRecordsById && (
            <AddMetricsToRunButton
              metricRecordsById={rerunMetricRecordsById}
              simulationOutputIds={allDrillInSimulationOutputIds}
              selectedSimulationOutputIds={selectedSimulationOutputIds}
              forcedMetricIds={[selectedMetricId]}
            />
          )}
        </div>
        <Card className="shadow-none border border-coval-border h-fit">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-coval-text">
              {resolveMetricDisplayName(
                metricRecord?.metric_display_name,
                metricRecord?.metric_name,
              )}
            </h2>
            {metricRecord?.description && (
              <p className="text-sm text-coval-text mt-1 mb-4">
                {metricRecord.description}
              </p>
            )}
            {!metricRecord?.description && <div className="mb-4" />}
            {inDepthContent}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div
      ref={setContainerEl}
      data-testid="metrics-section"
      className="flex flex-col gap-4 px-4 pt-4 pb-6 bg-surface-contrast"
    >
      <MetricLayoutSections
        groupedMetrics={groupedMetrics}
        metricLayout={metricLayout}
        editLayout={editLayout}
        onMetricLayoutChange={onMetricLayoutChange}
        useSingleColumn={useSingleColumn}
        renderPlotBlock={renderPlotBlock}
        createPlot={createPlot}
        onPlotAdded={onPlotAdded}
        renderCard={(metricId) => (
          <Card className="border border-coval-border shadow-none bg-background">
            <CardContent className="px-0 pt-0 pb-4">
              <MetricCardSection
                runRows={scopedRunRows}
                metricRecord={metricRenderAdaptersById[metricId]}
                onSelectMetric={
                  editLayout ? () => undefined : setSelectedMetricId
                }
                compareBy={compareBy ?? undefined}
              />
            </CardContent>
          </Card>
        )}
      />
    </div>
  )
}
